import { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import BoxControls from "../../../Common/Box/BoxControls";
import Figure from "../../../Common/Box/Figure";
import MethodChooser from "../../../Common/Box/Choosers/MethodChooser";
import { capitalizePhrase, getTooltip } from "../../../../utilities";
import useAreaQuery from "../../../../hooks/useAreaQuery";
import theme from "../../../../theme";

const number = 8;

const getSeries = (data) => {
  return [
    {
      name: "2019",
      data: data.map((country, index) => ({
        x: index,
        y: country.years[0].value,
      })),
      color: "#f69877",
      marker: {
        fillColor: "#f69877",
      },
    },
    {
      name: "2025",
      data: data.map((country, index) => ({
        x: index,
        y: country.years[1].value,
      })),
      color: "#f37447",
      marker: {
        fillColor: "#f37447",
      },
    },
    {
      name: "2030",
      data: data.map((country, index) => ({
        x: index,
        y: country.years[2].value,
      })),
      color: "#de6746",
      marker: {
        fillColor: "#de6746",
      },
    },
  ];
};

const drawRange = (chart, countryCount, countryIndex, y1, y2) => {
  const countryOffset = countryCount - countryIndex;

  const xAxis = chart.xAxis[0];
  const yAxis = chart.yAxis[0];

  const itemGap = xAxis.translate(countryCount - 1.5);
  const itemCenter = xAxis.top + xAxis.translate(countryOffset) + itemGap;

  chart.renderer
    .path([
      "M",
      yAxis.left + yAxis.translate(y1),
      itemCenter,
      "L",
      yAxis.left + yAxis.translate(y2),
      itemCenter,
    ])
    .attr({
      "z-index": -1,
      "stroke-width": 1,
      stroke: theme.palette.grays.gray4,
    })
    .add();
};

const drawRanges = (chart, data) => {
  const countryCount = data.length;

  for (let i = 0; i < countryCount; i++) {
    const y1 = Math.min(...data[i].years.map((year) => year.value));
    const y2 = Math.max(...data[i].years.map((year) => year.value));

    drawRange(chart, countryCount, i, y1, y2);
  }
};

const options = (data) => (theme) => {
  const series = getSeries(data);

  return {
    chart: {
      type: "scatter",
      width: 1000,
      height: 1000,
      inverted: true,
      backgroundColor: null,
      events: {
        redraw: function () {
          drawRanges(this, data, series);
        },
      },
    },
    xAxis: {
      categories: data.map((country) => country.name),
      title: undefined,
      labels: {
        autoRotation: undefined,
        useHTML: true,
        style: {
          color: theme.figure.axis.primary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    yAxis: {
      title: undefined,
      opposite: true,
      labels: {
        formatter: function () {
          return "$" + this.axis.defaultLabelFormatter.call(this);
        },
        style: {
          color: theme.figure.axis.secondary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    plotOptions: {
      series: {
        marker: {
          symbol: "circle",
          radius: 4,
          lineWidth: 1,
          lineColor: "silver",
        },
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      reversed: false,
      itemStyle: {
        fontSize: theme.figure.legend.fontSize,
      },
    },
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      outside: true,
      formatter: function () {
        return getTooltip("Cost", this.y, undefined, {
          prefix: "$",
        });
      },
      style: {
        fontSize: "0.875rem",
        color: theme.palette.tooltip.primary,
      },
    },
    exporting: {
      chartOptions: {
        title: undefined,
        subtitle: undefined,
      },
      enabled: false,
    },
    series,
  };
};

const useStyles = makeStyles((theme) => ({
  choosers: {
    display: "grid",
    gridAutoFlow: "column",
    gridColumnGap: theme.spacing(2),
    width: "fit-content",
  },
}));

const FpCcF8 = (props) => {
  const [selectedMethodId, setSelectedMethodId] = useState(0);

  const { areaData: data } = useAreaQuery(
    `/custom/fp/cc/figures/6/${props.regionId}/1/${selectedMethodId}`
  );

  const classes = useStyles();

  const handleMethodChange = (methodId) => {
    setSelectedMethodId(methodId);
  };

  return (
    <Figure
      id="FP_CC_f8"
      number={number.toString()}
      title="Change in Contraceptive Supply Costs"
      subtitle={`${capitalizePhrase(props.regionName)} by Country, 2019-2030`}
      chartOptions={data && options(data)}
      controls={
        <BoxControls>
          <div className={classes.choosers}>
            <MethodChooser
              area="contraception"
              value={selectedMethodId}
              onChange={handleMethodChange}
            />
          </div>
        </BoxControls>
      }
      annotations={true}
    />
  );
};

FpCcF8.propTypes = {
  regionId: PropTypes.number.isRequired,
  regionName: PropTypes.string.isRequired,
};

export default FpCcF8;
